import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ChannelIcon } from '../../components/ui/ChannelIcon';
import { destinationById } from '../../lib/publishing/destinations';
import type { WildcardContext } from '../../lib/publishing/captionWildcards';
import { previewTabs, previewTargetsFor } from '../../lib/publishing/perAccountPreview';
import type { AccountCoverage } from '../../lib/publishing/multiAccount';
import type { ComposerState } from './composerState';
import { PostPreviews } from './previews/PostPreviews';

// Post to Many — Preview ONE dealer's post through the REAL platform previews
// (customise brief, "Preview"; Customise is not built yet). Read-only.
//
// ⚠️ NO NEW RESOLUTION PATH. The caption is resolved by PostPreviews through
// resolveCaptionWildcards with THIS dealer's WildcardContext — the same context
// the list's row was resolved from (one batch read, wildcardContexts.ts), and
// the same resolver dispatch uses. So the dialog and the row cannot disagree.
//
// One tab per destination this dealer actually posts to (its coverage), each
// rendering only that destination. The shared composition is narrowed by the
// pure previewTargetsFor, which copies — this dialog cannot write into the
// state every other dealer reads from. The collage / carousel / link card come
// from the shared preview components, so media order and count read right.

export function PerAccountPreviewDialog({
  open,
  onOpenChange,
  accountName,
  coverage,
  context,
  state,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountName: string;
  coverage: AccountCoverage;
  context: WildcardContext;
  state: ComposerState;
}) {
  const tabs = previewTabs(coverage);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Preview for {accountName}</DialogTitle>
          <DialogDescription>
            What this account&rsquo;s post will look like, with its own merge fields filled in. Read-only.
          </DialogDescription>
        </DialogHeader>
        {tabs.length > 0 && (
          <Tabs defaultValue={tabs[0]}>
            <TabsList className="flex-wrap h-auto">
              {tabs.map((d) => {
                const dest = destinationById(d);
                return (
                  <TabsTrigger key={d} value={d} className="gap-1.5">
                    <ChannelIcon channel={dest.platform} className="h-3.5 w-3.5" />
                    {dest.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
            {tabs.map((d) => (
              <TabsContent key={d} value={d} className="pt-3">
                <PostPreviews
                  state={{ ...state, targets: previewTargetsFor(state.targets, coverage, d) }}
                  platforms={null}
                  issues={[]}
                  wildcard={context}
                  pageName={accountName}
                />
              </TabsContent>
            ))}
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
